// ── 5 Integrationen & Skills ── das Formular: ein Schalter je Integration
// (docs/11 8a), darunter ihre Felder; nach Herkunft gruppiert.

import type { IntegrationDef, Tenant } from "../../api/types";
import { Note } from "../../ui/badge";
import { Check, Field, Input } from "../../ui/field";
import { groupIntegrations, type IntegrationsState } from "./integrations";
import { fieldError, secretPlaceholder, Warnings } from "./shared";
import type { StepFormProps } from "./types";

export function IntegrationsForm({ tenant: t, catalog: cat, state, setState, check }: StepFormProps<IntegrationsState>) {
  const setEnabled = (key: string, on: boolean) => setState((s) => ({ ...s, enabled: { ...s.enabled, [key]: on } }));
  const setValue = (key: string, v: string, value: string) =>
    setState((s) => ({ ...s, values: { ...s.values, [key]: { ...s.values[key], [v]: value } } }));
  const toggleSkill = (dir: string, on: boolean) => setState((s) => ({ ...s, skills: on ? [...s.skills.filter((x) => x !== dir), dir] : s.skills.filter((x) => x !== dir) }));
  return (
    <>
      <Note>Ein Schalter je Integration setzt Env-Variablen bzw. Credential-Gruppe <strong>und</strong> ihre Skills (docs/11 8a). Env-Änderungen werden im Wartungsfenster wirksam.</Note>
      {groupIntegrations(cat.integrations).map(([title, defs]) => (
        <section className="pf-group" key={title}>
          <h3 className="pf-subhead">{title}</h3>
          <div className="pf-list">
            {defs.map((def) => (
              <Integration
                key={def.key} def={def} tenant={t} check={check} on={Boolean(state.enabled[def.key])}
                values={state.values[def.key] ?? {}} onToggle={(on) => setEnabled(def.key, on)} onValue={(v, value) => setValue(def.key, v, value)}
              />
            ))}
          </div>
        </section>
      ))}
      <section className="pf-group">
        <h3 className="pf-subhead">Skills ohne Integration</h3>
        <div className="pf-grid pf-grid--checks">
          {cat.standalone_skills.map((s) => (
            <Check
              key={s.dir} name="skills" value={s.dir} checked={state.skills.includes(s.dir)} onChange={(on) => toggleSkill(s.dir, on)}
              label={<>{s.name} <code>{s.dir}</code>{s.internal && <> <span className="pf-tag pf-tag--warn">Solutec-intern</span></>}</>}
              hint={s.description}
            />
          ))}
        </div>
      </section>
      <Warnings check={check} />
    </>
  );
}

function Integration({ def, tenant: t, check, on, values, onToggle, onValue }: {
  def: IntegrationDef;
  tenant: Tenant;
  check: StepFormProps<IntegrationsState>["check"];
  on: boolean;
  values: Record<string, string>;
  onToggle: (on: boolean) => void;
  onValue: (v: string, value: string) => void;
}) {
  // „braucht Entra" gelb, solange der Mandant (gespeichert) ohne Entra ist.
  const entra = t.auth.modes.includes("entra");
  const secretsSet = t.integration_secrets_set[def.key] ?? [];
  const groupErr = fieldError(check, def.key);
  return (
    <div className="pf-integration">
      <div className="pf-integration__head">
        <Check
          name={`int:${def.key}`} checked={on} onChange={onToggle}
          label={<>
            <strong>{def.name}</strong>
            {def.internal && <> <span className="pf-tag pf-tag--warn">Solutec-intern</span></>}
            {def.requiresEntra && <> <span className={`pf-tag${entra ? "" : " pf-tag--warn"}`}>braucht Entra</span></>}
            {def.skills.length > 0 && <> <span className="pf-tag">Skills: {def.skills.join(", ")}</span></>}
          </>}
          hint={<>{def.description}{def.note && <> <em>{def.note}</em></>}</>}
        />
      </div>
      <div className="pf-integration__fields pf-list" hidden={!on}>
        {groupErr && <Note tone="error">{groupErr}</Note>}
        <div className="pf-grid">
          {def.fields.map((f) => {
            const err = fieldError(check, `${def.key}.${f.var}`);
            const where = f.target === "env" ? "Server-Env, Neustart" : `Credential-Gruppe ${f.target.split(":")[1] ?? ""}`;
            return (
              <Field key={f.var} label={`${f.label}${f.required ? " *" : ""}`} error={err} hint={<><code>{f.var}</code> · {where}</>}>
                <Input
                  name={`val:${def.key}:${f.var}`} type={f.secret ? "password" : "text"} value={values[f.var] ?? ""} invalid={!!err}
                  placeholder={f.secret ? secretPlaceholder(secretsSet.includes(f.var)) : (f.placeholder ?? "")}
                  autoComplete={f.secret ? "new-password" : "off"} onChange={(e) => onValue(f.var, e.target.value)}
                />
              </Field>
            );
          })}
        </div>
      </div>
    </div>
  );
}
